/* eslint-disable camelcase */
import React, { useContext } from 'react';
import NextLink from 'next/link';
import classNames from 'classnames';
import { Link, RichText } from 'prismic-reactjs';
import { IPrismicImage, IPrismicText, IPrismicUrl } from '@lib/prismic/types';
import { asText } from '@lib/prismic/util/document';
import parseUrl from '@lib/prismic/util/parseUrl';
import breakpoints from '@constants/breakpoints';
import PrismicImage from '@components/shared/images/atoms/Image/PrismicImage';
import { useTheme } from '@components/theme';
import If from '@components/shared/conditional/If/If';
import UserContext from '@context/User/user-context';
import styles from './FullBleedHero.module.scss';

interface IFullBleedHero {
  hero_title: IPrismicText[];
  hero_subtitle?: IPrismicText[];
  hero_description?: IPrismicText[];
  hero_eyebrow?: IPrismicText[];
  hero_cta_text?: IPrismicText[];
  hero_cta_link?: IPrismicUrl;
  hero_cta_logged_in_link?: IPrismicUrl;
  main_hero_image: IPrismicImage;
  ctaOnClick?: (e: any) => void;
  className?: string;
  subtitleIsH1?: boolean;
  prefetchHeroCtaLink?: boolean;
  ctaDataTest?: string;

}

export default function FullBleedHero({
  hero_title,
  hero_subtitle,
  hero_description,
  hero_eyebrow,
  hero_cta_text,
  hero_cta_link,
  hero_cta_logged_in_link,
  main_hero_image,
  ctaOnClick = () => {},
  className = '',
  subtitleIsH1 = false,
  prefetchHeroCtaLink = false,
  ctaDataTest = ''
}: IFullBleedHero) {
  const theme = useTheme();
  const { user } = useContext(UserContext);

  return (
    <section className={classNames(styles.FullBleedHero, className)}>

      <div className={styles.AspectRatioBox}>
        <div className={styles.AspectRatioBoxInside}>

          <PrismicImage
            image={main_hero_image}
            lazy={false}
            noAspectRatioBox
            breakpoints={{
              mobile: breakpoints['small-tablet'],
              desktop: breakpoints.desktop
            }}
          />

        </div>
      </div>

      <div className={ styles.ContentContainer }>
        <If condition={!!hero_eyebrow}>
          <div
            className={ classNames(
              theme.styles.Overline,
              styles.Eyebrow
            )}
          >
            { asText(hero_eyebrow!) }
          </div>
        </If>

        <TitleSubtitle
          title={hero_title}
          subTitle={hero_subtitle}
          subtitleIsH1={subtitleIsH1}
        />

        <If condition={!!hero_description}>
          <div
            className={classNames(
              theme.styles.Body,
              styles.Description
            )}
          >
            { RichText.render(hero_description!) }
          </div>
        </If>

        <If condition={!!hero_cta_text}>
          <NextLink
            href={
              user && hero_cta_logged_in_link
                ? parseUrl(Link.url(hero_cta_logged_in_link))
                : parseUrl(Link.url(hero_cta_link))
            }
            prefetch={prefetchHeroCtaLink}
          >
            {/* eslint-disable */}
            <a
              className={classNames(theme.styles.Button,
                theme.styles.Primary,
                theme.styles.Medium,
                styles.CTAButton)}
              data-test={ctaDataTest || 'full-bleed-hero-cta-link'}
              role="button"
              onClick={ctaOnClick}
            >
              {/* @ts-ignore */}
              {asText(hero_cta_text)}
            </a>
            {/* eslint-enable */}
          </NextLink>

        </If>
      </div>
    </section>
  );
}

function TitleSubtitle({
  subtitleIsH1,
  title,
  subTitle
}: {
  subtitleIsH1?: boolean;
  title: IPrismicText[];
  subTitle?: IPrismicText[];
}) {
  if (subTitle && subtitleIsH1) {
    return (
      <>
        <h2 className={ classNames(styles.Title) }>
          {asText(title)}
        </h2>
        {subTitle && (
          <h1 className={ classNames(styles.Subtitle) }>
            {asText(subTitle)}
          </h1>
        )}
      </>
    );
  }
  return (
    <>
      <h1 className={ classNames(styles.Title) }>
        {asText(title)}
      </h1>
      {subTitle && (
        <h2 className={ classNames(styles.Subtitle) }>
          {asText(subTitle)}
        </h2>
      )}
    </>
  );
}
